import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import MenuIcon from '@mui/icons-material/Menu'
import { Box, IconButton, Toolbar, Typography } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router'

import { getAppTitle } from '../../config/environment'
import routes from '../../pages/routes'
import Database from '../../types/Database'

interface AppBarHeaderProps {
  user: Database.User | null
  onDrawerOpen: () => void
  drawerOpen: boolean
}

/**
 * AppBarHeader - Header section of the application bar
 *
 * Contains the logo, title, user info, and menu button.
 * Focuses on the top navigation bar presentation.
 */
export default function AppBarHeader({
  user,
  onDrawerOpen,
  drawerOpen
}: AppBarHeaderProps) {
  const navigate = useNavigate()

  const handleLogoClick = () => {
    navigate(routes.home, { replace: true })
  }

  const handleProfileClick = () => {
    navigate(routes.profile)
  }

  return (
    <Toolbar sx={{ minHeight: '64px', px: { xs: 2, sm: 4 }, py: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'transform 0.2s ease-in-out',
          mr: { xs: 1, sm: 3 },
          '&:hover': {
            transform: 'scale(1.05)'
          }
        }}
        onClick={handleLogoClick}
      >
        <img
          src='/logo.png'
          alt='logo'
          style={{
            width: 45,
            height: 45,
            marginRight: 16,
            filter:
              'brightness(0) invert(1) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
          }}
        />
        <Typography
          variant='h6'
          noWrap
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontWeight: 600,
            letterSpacing: '0.5px',
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            transition: 'opacity 0.2s ease-in-out',
            '&:hover': {
              opacity: 0.9
            }
          }}
          component='div'
        >
          {getAppTitle()}
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      {user && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 2,
            px: { xs: 1.5, sm: 3 },
            py: 1.5,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
            }
          }}
          onClick={handleProfileClick}
        >
          <AccountCircleIcon
            sx={{
              fontSize: 28,
              color: '#2563eb'
            }}
          />
          <Typography
            variant='body1'
            noWrap
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 500,
              color: '#0f172a'
            }}
            component='div'
          >
            {user.firstname} {user.lastname}
          </Typography>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='end'
            onClick={e => {
              e.stopPropagation() // Prevent profile navigation when clicking menu
              onDrawerOpen()
            }}
            data-testid='main-menu'
            sx={{
              ...(drawerOpen && { display: 'none' }),
              color: '#0f172a',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
                transform: 'scale(1.1)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}
    </Toolbar>
  )
}
